import React, { useState } from 'react';
import api from '../utils/api';

function Exporte() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [format, setFormat] = useState('csv');
  const [includeOpenDays, setIncludeOpenDays] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastExport, setLastExport] = useState(null);

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setMessage({ type: 'error', text: 'Bitte Start- und Enddatum wählen' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const response = await api.post('/api/exports/generate', {
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate + 'T23:59:59').toISOString(),
        format: format,
        include_open_days: includeOpenDays
      });

      setLastExport(response);

      // Download file
      const blob = new Blob([response.content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', response.filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setMessage({
        type: 'success',
        text: `✓ Export erfolgreich: ${response.closed_count} Buchungen aus ${response.closeout_count} abgeschlossenen Tagen${
          includeOpenDays ? ` + ${response.open_count} offene Buchungen` : ''
        }`
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Exporte</h1>
        <p className="page-subtitle">DATEV, Lexware & CSV-Exporte für Buchhaltung</p>
      </div>

      {/* Message */}
      {message && (
        <div className={message.type === 'success' ? 'alert alert-success' : 'alert alert-error'}>
          {message.text}
        </div>
      )}

      {/* Info Box */}
      <div className="alert alert-info mb-8">
        <div style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>
          ℹ️ Wichtig: Datenherkunft
        </div>
        <div style={{ fontSize: '0.875rem', lineHeight: 'var(--leading-relaxed)' }}>
          <div style={{ marginBottom: 'var(--space-2)' }}>
            <strong>Abgeschlossene Tage:</strong> Daten stammen aus Z-Berichten (Kassenabschluss). Dies ist die
            Quelle der Wahrheit für Steuerberater.
          </div>
          <div style={{ marginBottom: 'var(--space-2)' }}>
            <strong>Offene Tage:</strong> Optional einbeziehbar, werden aber separat markiert. Nicht für offizielle
            Buchhaltung verwenden.
          </div>
          <div style={{ marginTop: 'var(--space-3)', fontWeight: 600 }}>
            👉 Exporte ändern nie Daten. Sie sind read-only.
          </div>
        </div>
      </div>

      {/* Export Form */}
      <div className="dark-card mb-8">
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 'var(--space-6)' }}>
          Export erstellen
        </h2>

        {/* Date Range */}
        <div className="grid-2 mb-6">
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
              Von (Datum)
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="dark-input"
              data-testid="export-start-date"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
              Bis (Datum)
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="dark-input"
              data-testid="export-end-date"
            />
          </div>
        </div>

        {/* Format Selection */}
        <div className="mb-6">
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-3)', color: 'var(--text-primary)' }}>
            Export-Format
          </label>
          <div className="grid-2">
            <button
              onClick={() => setFormat('csv')}
              style={{
                padding: 'var(--space-4)',
                border: format === 'csv' ? '2px solid var(--accent-primary)' : '2px solid var(--border-primary)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'left',
                transition: 'all var(--transition-fast)',
                background: format === 'csv' ? 'var(--accent-muted)' : 'var(--bg-tertiary)',
                cursor: 'pointer'
              }}
              data-testid="format-csv"
            >
              <div style={{ fontWeight: 600, marginBottom: 'var(--space-1)', color: 'var(--text-primary)' }}>
                CSV (Allgemein)
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Für Excel, LibreOffice oder eigene Weiterverarbeitung
              </div>
            </button>

            <button
              onClick={() => setFormat('datev')}
              style={{
                padding: 'var(--space-4)',
                border: format === 'datev' ? '2px solid var(--accent-primary)' : '2px solid var(--border-primary)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'left',
                transition: 'all var(--transition-fast)',
                background: format === 'datev' ? 'var(--accent-muted)' : 'var(--bg-tertiary)',
                cursor: 'pointer'
              }}
              data-testid="format-datev"
            >
              <div style={{ fontWeight: 600, marginBottom: 'var(--space-1)', color: 'var(--text-primary)' }}>
                DATEV ASCII
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Für Import in DATEV Kanzlei-Rechnungswesen
              </div>
            </button>
          </div>
        </div>

        {/* Options */}
        <div className="mb-6">
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeOpenDays}
              onChange={(e) => setIncludeOpenDays(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
              data-testid="include-open-days"
            />
            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
              Offene Tage einbeziehen (nicht abgeschlossene Tage separat markiert)
            </span>
          </label>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginLeft: '24px', marginTop: 'var(--space-1)' }}>
            ⚠️ Offene Tage sollten nicht für offizielle Buchhaltung verwendet werden
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleExport}
          disabled={loading || !startDate || !endDate}
          className="btn-primary"
          style={{ width: '100%' }}
          data-testid="generate-export-button"
        >
          {loading ? 'Erstelle Export...' : 'Export erzeugen & herunterladen'}
        </button>
      </div>

      {/* Last Export Info */}
      {lastExport && (
        <div className="dark-card">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 'var(--space-6)', color: 'var(--text-primary)' }}>
            Letzter Export
          </h3>
          <div className="grid-2">
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                Format
              </div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {lastExport.format.toUpperCase()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                Dateiname
              </div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {lastExport.filename}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                Abgeschlossene Buchungen
              </div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {lastExport.closed_count}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                Abgeschlossene Tage (Z-Berichte)
              </div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {lastExport.closeout_count}
              </div>
            </div>
            {lastExport.open_count > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--warning-text)', marginBottom: 'var(--space-1)' }}>
                  ⚠️ Offene Buchungen (nicht abgeschlossen)
                </div>
                <div style={{ fontWeight: 600, color: 'var(--warning-text)' }}>
                  {lastExport.open_count}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Account Mapping Section */}
      <div className="dark-card mt-6">
        <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
          Kontenzuordnung
        </h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-6)' }}>
          Standardzuordnung für DATEV-Export. Diese Werte werden verwendet, wenn keine spezifische Zuordnung
          konfiguriert ist.
        </p>
        <div className="grid-2">
          <div>
            <div style={{ fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--text-primary)' }}>
              Zahlungsarten
            </div>
            <div style={{ fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--border-primary)' }}>
                <span style={{ color: 'var(--text-primary)' }}>Bargeld</span>
                <span style={{ color: 'var(--text-secondary)' }}>Konto 1000</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-2) 0' }}>
                <span style={{ color: 'var(--text-primary)' }}>Karte / EC</span>
                <span style={{ color: 'var(--text-secondary)' }}>Konto 1200</span>
              </div>
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--text-primary)' }}>
              Steuersätze
            </div>
            <div style={{ fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--border-primary)' }}>
                <span style={{ color: 'var(--text-primary)' }}>19% MwSt</span>
                <span style={{ color: 'var(--text-secondary)' }}>Konto 8400</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--border-primary)' }}>
                <span style={{ color: 'var(--text-primary)' }}>7% MwSt</span>
                <span style={{ color: 'var(--text-secondary)' }}>Konto 8300</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-2) 0' }}>
                <span style={{ color: 'var(--text-primary)' }}>0% MwSt</span>
                <span style={{ color: 'var(--text-secondary)' }}>Konto 8100</span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 'var(--space-4)', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
          💡 Diese Zuordnung kann später in den Einstellungen angepasst werden
        </div>
      </div>
    </div>
  );
}

export default Exporte;
